import { useState } from "react"
import { motion } from "framer-motion"
import { Check } from "lucide-react"
import { useStudioStore } from "@/store/studio"
import { cn } from "@/lib/utils"
import type { Shot } from "@/types"
import { useLocation } from "wouter"

const qualityLabels = ["Draft · Fast exploration", "Review · Balanced quality", "Final · Cinema-grade"]

export default function PlanView() {
  const [, navigate] = useLocation()
  const store = useStudioStore()
  const [selectedScene, setSelectedScene] = useState(0)
  const [selectedShot, setSelectedShot] = useState<{ si: number; ji: number } | null>(null)
  const [showPreflight, setShowPreflight] = useState(false)
  const [spendLimit, setSpendLimit] = useState(store.budget)
  const [inspectingShot, setInspectingShot] = useState<Shot | null>(null)

  const scenes = store.story?.scenes || []
  const currentScene = scenes[selectedScene]
  const totalShots = scenes.reduce((n, s) => n + s.shots.length, 0)
  const renderedSeconds = scenes.flatMap((scene) => scene.shots).reduce((total, shot) => total + (shot.duration <= 5 ? 5 : shot.duration <= 10 ? 10 : 15), 0)
  const videoRate = store.quality === 0 ? 0.10 : 0.15
  const videoCost = renderedSeconds * videoRate
  const continuityCost = totalShots ? 0.03 + totalShots * 0.02 : 0
  const masterCost = 0
  const totalCost = videoCost + continuityCost + masterCost

  const handleApprovePlan = () => {
    store.setBudget(spendLimit)
    store.setPlanHash(store.storyHash)
    store.setPlanApproved(true)
    store.setStage(Math.max(store.stage, 2))
    setShowPreflight(false)
    window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "Plan approved. Ready to produce." } }))
    navigate("/studio/produce")
  }

  if (!scenes.length) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
        <p className="text-lg text-[#9ca3af] mb-2">No shot plan yet</p>
        <p className="text-sm text-[#6b7280]">Build and greenlight the story before planning shots.</p>
      </motion.div>
    )
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-1 text-[#f59e0b]">Stage 2 · Plan</p>
          <h1 className="heading-display text-3xl">See the film before you render it.</h1>
          <p className="text-sm text-[#9ca3af] mt-1">Every shot is editable, priced, and connected to the story.</p>
        </div>
      </div>

      {/* Quality */}
      <div className="surface p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="w-full sm:flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6b7280] mb-1">Quality profile</p>
          <p className="text-sm font-medium text-[#f0f1f5]">{qualityLabels[store.quality]}</p>
        </div>
        <div className="w-full sm:flex-1">
          <input
            type="range"
            min={0}
            max={2}
            step={1}
            value={store.quality}
            onChange={(e) => store.setQuality(Number(e.target.value) as 0 | 1 | 2)}
            className="w-full accent-[#f59e0b]"
          />
          <div className="flex justify-between text-[10px] text-[#6b7280] mt-1">
            <span>Draft</span>
            <span>Review</span>
            <span>Final</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr_280px] gap-4">
        {/* Scene List */}
        <div className="surface p-3 space-y-1 max-h-[600px] overflow-y-auto">
          {scenes.map((s, i) => (
            <button
              key={i}
              onClick={() => { setSelectedScene(i); setSelectedShot(null); setInspectingShot(null) }}
              className={cn(
                "w-full text-left p-2.5 rounded-lg text-xs transition-colors",
                selectedScene === i ? "bg-[#f59e0b]/10 text-[#f59e0b]" : "text-[#9ca3af] hover:bg-[#0f1117]"
              )}
            >
              <p className="font-medium">Scene {String(i + 1).padStart(2, "0")}</p>
              <p className="text-[10px] text-[#6b7280] mt-0.5">{s.shots.length} shots · {s.shots.reduce((a, x) => a + x.duration, 0)} sec</p>
            </button>
          ))}
        </div>

        {/* Storyboard */}
        <div className="surface p-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
            <div>
              <h2 className="heading-sans text-lg">Scene {selectedScene + 1} · {currentScene?.title}</h2>
              <p className="text-xs text-[#9ca3af]">{currentScene?.summary}</p>
            </div>
            <span className="text-xs text-[#6b7280]">~${(((currentScene?.shots || []).reduce((sum, shot) => sum + (shot.duration <= 5 ? 5 : shot.duration <= 10 ? 10 : 15), 0) * videoRate) + (currentScene?.shots.length || 0) * 0.02 + (selectedScene === 0 ? 0.03 : 0)).toFixed(2)} cloud · $0 local</span>
          </div>

          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 gap-3">
            {currentScene?.shots.map((shot, j) => (
              <button
                key={j}
                onClick={() => { setSelectedShot({ si: selectedScene, ji: j }); setInspectingShot(shot) }}
                className={cn(
                  "text-left p-3 rounded-lg border transition-all",
                  selectedShot?.si === selectedScene && selectedShot?.ji === j
                    ? "border-[#f59e0b]/40 bg-[#f59e0b]/[0.02]"
                    : "border-[#1e2130] bg-[#0f1117] hover:border-[#2d3148]"
                )}
              >
                <div className="aspect-video bg-[#161821] rounded-md mb-2 flex items-center justify-center">
                  <span className="text-[10px] font-mono text-[#6b7280]">SC {String(selectedScene + 1).padStart(2, "0")} · SH {String(j + 1).padStart(2, "0")}</span>
                </div>
                <p className="text-xs font-medium text-[#f0f1f5] truncate">{j + 1}. {shot.camera}</p>
                <p className="text-[10px] text-[#6b7280]">{shot.duration}s</p>
              </button>
            ))}
          </div>
        </div>

        {/* Shot Inspector */}
        <div className="surface p-4">
          {inspectingShot ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="heading-sans text-sm">Shot {selectedShot!.ji + 1}</h3>
                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#f59e0b]/10 text-[#f59e0b]">Editable</span>
              </div>
              <label className="block">
                <span className="text-xs text-[#9ca3af] mb-1 block">Action</span>
                <textarea className="input" rows={5} defaultValue={inspectingShot.subject} />
              </label>
              <label className="block">
                <span className="text-xs text-[#9ca3af] mb-1 block">Camera</span>
                <input className="input" defaultValue={inspectingShot.camera} />
              </label>
              <label className="block">
                <span className="text-xs text-[#9ca3af] mb-1 block">Duration (seconds)</span>
                <input className="input" type="number" min={1} max={20} defaultValue={inspectingShot.duration} />
              </label>
              <label className="block">
                <span className="text-xs text-[#9ca3af] mb-1 block">Generation note</span>
                <textarea className="input" rows={3} placeholder="What must this take preserve?" />
              </label>
              <button className="btn-primary w-full">Apply changes</button>
              <p className="text-[10px] text-[#6b7280]">Only this shot and its descendants will rebuild. Estimated change: $0.14.</p>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-sm text-[#9ca3af]">Select a shot to direct it.</p>
            </div>
          )}
        </div>
      </div>

      {/* Greenlight */}
      <div className="gate-bar mt-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#10b981]/10 flex items-center justify-center">
            <Check className="w-4 h-4 text-[#10b981]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#f0f1f5]">Animatic ready for review</p>
            <p className="text-xs text-[#9ca3af]">Watch the full rhythm before final-quality spend.</p>
          </div>
        </div>
        <button onClick={() => store.planApproved && store.budget >= totalCost ? navigate("/studio/produce") : setShowPreflight(true)} className="btn-primary">
          {store.planApproved && store.budget >= totalCost ? "Open production →" : "Review pre-flight →"}
        </button>
      </div>

      {/* Pre-flight Modal */}
      {showPreflight && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(8, 9, 13, 0.8)" }} onClick={() => setShowPreflight(false)}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto surface-elevated p-4 sm:p-6"
          >
            <p className="eyebrow mb-2">Pre-flight report</p>
            <h2 className="heading-serif text-2xl mb-6">Approve the plan, not a blank cheque.</h2>

            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
              <div className="text-center p-3 rounded-lg bg-[#0f1117] border border-[#1e2130]">
                <p className="text-2xl font-semibold text-[#f0f1f5]">{totalShots}</p>
                <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">Planned shots</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-[#0f1117] border border-[#1e2130]">
                <p className="text-2xl font-semibold text-[#f0f1f5]">{scenes.length}</p>
                <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">Scenes</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-[#0f1117] border border-[#1e2130]">
                <p className="text-sm font-semibold text-[#f0f1f5] mt-1.5">{["Draft", "Review", "Final"][store.quality]}</p>
                <p className="text-[10px] text-[#6b7280] uppercase tracking-wider mt-1">Quality</p>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <div className="flex justify-between text-sm py-2 border-b border-[#1e2130]">
                <span className="text-[#9ca3af]">Video generation estimate</span>
                <b className="text-[#f0f1f5]">${videoCost.toFixed(2)}</b>
              </div>
              <div className="flex justify-between text-sm py-2 border-b border-[#1e2130]">
                <span className="text-[#9ca3af]">Cast lock & shot keyframes</span>
                <b className="text-[#f0f1f5]">${continuityCost.toFixed(2)}</b>
              </div>
              <div className="flex justify-between text-sm py-2 border-b border-[#1e2130]">
                <span className="text-[#9ca3af]">Forge assembly</span>
                <b className="text-[#f0f1f5]">Included</b>
              </div>
              <div className="flex justify-between text-base font-semibold py-2">
                <span className="text-[#f0f1f5]">Estimated upper bound</span>
                <b className="text-[#f59e0b]">${totalCost.toFixed(2)}</b>
              </div>
            </div>

            <label className="block mb-6">
              <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Hard spend limit</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#9ca3af]">$</span>
                <input type="number" min={totalCost} step={0.5} value={spendLimit} onChange={(e) => setSpendLimit(Number(e.target.value))} className="input" />
              </div>
            </label>

            {spendLimit < totalCost ? <p className="text-xs text-red-300 mb-6 rounded-lg border border-red-500/20 bg-red-500/5 p-3">Raise the limit to at least ${totalCost.toFixed(2)}, or keep editing and shorten the shot plan. Atlas will not begin a production it cannot finish within your limit.</p> : <p className="text-xs text-[#6b7280] mb-6">This estimate is calculated from the current shot plan. Atlas pauses before crossing your hard limit, and unused reservations are released.</p>}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button onClick={() => setShowPreflight(false)} className="btn-subtle">Keep editing</button>
              <button onClick={handleApprovePlan} disabled={spendLimit < totalCost} className="btn-primary">Approve & open production →</button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  )
}
